/**
 *  @class
 *  @function CollectionTabs
 */

if (!customElements.get('collection-tabs')) {
  class CollectionTabs extends HTMLElement {
    constructor() {
      super();
      this._onBlockSelect = null;
      this._fetchCache = new Map();
      this._fetchPromises = new Map();
    }

    connectedCallback() {
      requestAnimationFrame(() => {
        this.buttons = Array.from(this.querySelectorAll('button'));
        this.sectionHeader = this.closest('.section-header');
        if (!this.sectionHeader) return;
        this.links = Array.from(this.sectionHeader.querySelectorAll('.linked-to-tab'));
        this.target = this.dataset.target;
        this.slider = document.getElementById(this.target);
        this.themeScroll = this.sectionHeader.nextElementSibling;

        if (!this.slider) return;

        this.productLimit = parseInt(this.slider.dataset.productLimit, 10) || 6;

        this.collectionMap = new Map();
        this.allItems = this.slider.querySelectorAll('[data-collection]');
        this.allItems.forEach((item) => {
          let handle = item.dataset.collection;
          if (!this.collectionMap.has(handle)) {
            this.collectionMap.set(handle, []);
          }
          this.collectionMap.get(handle).push(item);
        });

        this.addEventListener('click', (event) => {
          let button = event.target.closest('button');
          if (!button) return;
          event.preventDefault();
          let index = this.buttons.indexOf(button);
          if (index === -1) return;
          this.toggleButtons(index);
          this.toggleLinks(index);
          if (button.dataset.collection) {
            this.handleTabClick(button);
          }
        });

        if (Shopify.designMode) {
          this._onBlockSelect = (event) => {
            const index = this.buttons.indexOf(event.target);
            if (index !== -1) {
              this.buttons[index].dispatchEvent(new Event('click'));
            }
          };
          this.addEventListener('shopify:block:select', this._onBlockSelect);
        }

        if (!Shopify.designMode) {
          this.prefetchInactiveTabs();
        }
      });
    }

    disconnectedCallback() {
      if (this._onBlockSelect) {
        this.removeEventListener('shopify:block:select', this._onBlockSelect);
      }
    }

    toggleButtons(index) {
      this.buttons.forEach((button) => button.classList.remove('active'));
      this.buttons[index].classList.add('active');
    }

    toggleLinks(index) {
      if (this.links.length && this.links[index]) {
        this.links.forEach((link) => link.classList.remove('active'));
        this.links[index].classList.add('active');
      }
    }

    toggleCollection(handle) {
      if (!this.slider) return;

      this.allItems.forEach((item) => item.classList.remove('carousel__slide'));
      let activeItems = this.collectionMap.get(handle);
      if (activeItems) {
        activeItems.forEach((item) => item.classList.add('carousel__slide'));
      }

      if (!this.themeScroll) return;
      this.themeScroll.init();
      let inner = this.themeScroll.querySelector('.theme-scroll--inner');
      if (inner) inner.scroll({ top: 0, left: 0 });
    }

    handleTabClick(button) {
      let handle = button.dataset.collection;
      if (this.collectionMap.has(handle)) {
        this.toggleCollection(handle);
        return;
      }
      let url = button.dataset.collectionUrl;
      if (!url) {
        this.toggleCollection(handle);
        return;
      }
      this.fetchCollection(handle, url).then(() => {
        this.toggleCollection(handle);
      });
    }

    prefetchInactiveTabs() {
      let loadTabs = () => {
        this.buttons.forEach((button, index) => {
          if (index === 0) return;
          let handle = button.dataset.collection;
          let url = button.dataset.collectionUrl;
          if (!handle || !url || this.collectionMap.has(handle)) return;
          this.fetchCollection(handle, url);
        });
      };

      if (document.readyState === 'complete') {
        loadTabs();
      } else {
        window.addEventListener('load', loadTabs, { once: true });
      }
    }

    fetchCollection(handle, collectionUrl) {
      if (this._fetchCache.has(handle)) {
        return Promise.resolve();
      }
      if (this._fetchPromises.has(handle)) {
        return this._fetchPromises.get(handle);
      }

      let fetchUrl = collectionUrl + '?section_id=collection-tabs-ajax';
      let promise = fetch(fetchUrl)
        .then((response) => response.text())
        .then((html) => {
          let parser = new DOMParser();
          let doc = parser.parseFromString(html, 'text/html');
          let items = Array.from(doc.querySelectorAll('[data-collection]')).slice(0, this.productLimit);

          items.forEach((item) => {
            this.slider.appendChild(item);
          });

          this.slider.querySelectorAll('.lazyload').forEach((img) => {
            lazySizes.loader.unveil(img);
          });

          if (!this.collectionMap.has(handle)) {
            this.collectionMap.set(handle, []);
          }
          items.forEach((item) => {
            this.collectionMap.get(handle).push(item);
          });
          this.allItems = this.slider.querySelectorAll('[data-collection]');
          this._fetchCache.set(handle, true);
          this._fetchPromises.delete(handle);
        })
        .catch(() => {
          this._fetchPromises.delete(handle);
        });

      this._fetchPromises.set(handle, promise);
      return promise;
    }
  }
  customElements.define('collection-tabs', CollectionTabs);
}

/* ===== Collection Tabs: desktop arrow scroller (ported from live) ===== */
(function () {
  var ENABLE_MIN_WIDTH = 1024; // PCのみ。モバイルはCSSの overflow-x:auto でタッチスクロール
  var STEP = 400; // 1回のスクロール量(px)

  function setup(el) {
    if (!el || el.closest('.collection-tabs--with-arrows')) return; // 二重ラップ防止（冪等）

    var wrapper = document.createElement('div');
    wrapper.className = 'collection-tabs--with-arrows';

    var prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'collection-tabs__arrow collection-tabs__arrow--prev';
    prev.setAttribute('aria-label', 'Scroll left');
    prev.innerHTML =
      '<svg width="8" height="14" viewBox="0 0 8 14" fill="none" aria-hidden="true" focusable="false">' +
      '<path d="M6.5 1.25L1.25 7L6.5 12.75" stroke="currentColor" stroke-width="1.0" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    var next = document.createElement('button');
    next.type = 'button';
    next.className = 'collection-tabs__arrow collection-tabs__arrow--next';
    next.setAttribute('aria-label', 'Scroll right');
    next.innerHTML =
      '<svg width="8" height="14" viewBox="0 0 8 14" fill="none" aria-hidden="true" focusable="false">' +
      '<path d="M1.5 1.25L6.75 7L1.5 12.75" stroke="currentColor" stroke-width="1.0" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    var parent = el.parentNode;
    parent.insertBefore(wrapper, el);
    wrapper.appendChild(prev);
    wrapper.appendChild(el);
    wrapper.appendChild(next);

    function updateEdge() {
      var maxScroll = el.scrollWidth - el.clientWidth;
      var x = el.scrollLeft;
      if (x <= 0) {
        prev.classList.add('is-disabled');
      } else {
        prev.classList.remove('is-disabled');
      }
      if (x >= maxScroll - 1) {
        next.classList.add('is-disabled');
      } else {
        next.classList.remove('is-disabled');
      }
    }

    function scrollBy(dx) {
      try {
        el.scrollBy({ left: dx, behavior: 'smooth' });
      } catch (e) {
        el.scrollLeft += dx;
      }
    }

    prev.addEventListener('click', function () {
      scrollBy(-STEP);
    });
    next.addEventListener('click', function () {
      scrollBy(+STEP);
    });

    el.addEventListener('scroll', updateEdge);
    window.addEventListener('resize', updateEdge);
    updateEdge();
  }

  function init() {
    if (window.innerWidth < ENABLE_MIN_WIDTH) return;
    var lists = document.querySelectorAll('.collection-tabs__list');
    Array.prototype.forEach.call(lists, setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
